import { useEffect, useState } from 'react';
import { TextField, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { Link } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { useSnackbar } from '../../../../Context/SnackbarContext';
import { TextBlock } from '../../../Atoms/Text';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { TabPanel } from '../../../Atoms/TabPanel';
import { searchLinks, bulkDeleteLinkData, SearchLinkResponse } from '../../../../Api/Link/LinkApi';
import { DetailViewCard } from '../../CardWithTitle/DetailViewCard';
import { PageLayout } from '../../../PageLayout';
import { BulkArchiveRPMGridColumnDefs } from './BulkArchiveRPMGridColumnDefs';
import { BulkArchiveLinkGridColumnDefs } from './BulkArchiveLinkGridColumnDefs';

const defaultBulkArchiveResult: SearchLinkResponse = {
  activeLinks: [],
  archivedLinks: [],
  unlinkedRpmAccounts: [],
  missingRpmAccounts: []
};

export const BulkArchiveLinks = () => {
  const { setSnack } = useSnackbar();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [rpmAccountIds, setRpmAccountIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bulkArchiveResult, setBulkArchiveResult] = useState<SearchLinkResponse>(defaultBulkArchiveResult);
  const [selectedTab, setSelectedTab] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [archivedCount, setArchivedCount] = useState(0);

  useEffect(() => {
    // Reset state when component mounts
    setCurrentStep(1);
    setRpmAccountIds([]);
    setBulkArchiveResult(defaultBulkArchiveResult);
    setSelectedTab(0);
    setErrorMessage('');
    setArchivedCount(0);
  }, []);

  const getNonEmptyIdsCount = () => {
    return rpmAccountIds.filter((id) => id.trim() !== '').length;
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      setIsSubmitting(true);

      try {
        const filteredIds = rpmAccountIds.filter((id) => id.trim() !== '');

        const response = await searchLinks({ rpmAccountIds: filteredIds });

        if (response.error) {
          throw new Error(response.error.message || 'Failed to fetch link information');
        }

        if (response.data) {
          setBulkArchiveResult(response.data);
          setCurrentStep(2);
        }
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleArchive = async () => {
    setIsSubmitting(true);

    try {
      const activeLinkIds = bulkArchiveResult.activeLinks.map((link) => link.id).filter((id): id is string => !!id);
      const countBeforeArchive = activeLinkIds.length;
      setArchivedCount(countBeforeArchive);

      const response = await bulkDeleteLinkData(activeLinkIds);

      if (response.error) {
        throw new Error(response.error.message || 'Failed to archive links');
      }

      // Show success toast
      setSnack({
        message: `${countBeforeArchive} Link${countBeforeArchive === 1 ? '' : 's'} Archived`,
        type: 'success',
        open: true
      });

      // Update the lists - use the archived links returned from the API
      const newlyArchivedLinks = response.data || [];

      setBulkArchiveResult((prev) => {
        const combinedArchivedLinks = [...prev.archivedLinks, ...newlyArchivedLinks];

        // Sort the combined archived links by RPM Account ID
        combinedArchivedLinks.sort((a, b) => {
          const aRpmId = a.rpmAccountId || 0;
          const bRpmId = b.rpmAccountId || 0;
          return aRpmId - bRpmId;
        });

        return {
          ...prev,
          activeLinks: [],
          archivedLinks: combinedArchivedLinks
        };
      });

      // Switch to the Archived tab to show the results
      setSelectedTab(1);

      // Move to step 3
      setCurrentStep(3);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isNextDisabled = getNonEmptyIdsCount() === 0 || getNonEmptyIdsCount() > 1000 || isSubmitting;

  const activeLinkCount = bulkArchiveResult.activeLinks.length;

  // Combined archive button for steps 2 and 3
  const archiveButton = (
    <button
      data-testid={currentStep === 2 ? 'archive-button' : 'archive-button-done'}
      disabled={currentStep === 2 ? activeLinkCount === 0 || isSubmitting : true}
      onClick={currentStep === 2 ? handleArchive : undefined}
      className={
        (currentStep === 2 && (activeLinkCount === 0 || isSubmitting)) || currentStep === 3
          ? DEFAULT_CANCEL_BUTTON_STYLE
          : DEFAULT_ADVANCE_BUTTON_STYLE
      }
      type="button">
      {currentStep === 2 && isSubmitting
        ? 'Archiving...'
        : `Archive (${currentStep === 2 ? activeLinkCount : archivedCount})`}
    </button>
  );

  // Create simplified columns for Link data (active and archived links)
  const linkColumns: GridColDef[] = BulkArchiveLinkGridColumnDefs;

  // Create simplified columns for RPMAccount data (unlinked accounts)
  const rpmAccountColumns: GridColDef[] = BulkArchiveRPMGridColumnDefs;

  // Simple columns for missing RPM account IDs (string array)
  const missingAccountColumns: GridColDef[] = [
    { field: 'rpmAccountId', headerName: 'RPM Account ID', flex: 1, minWidth: 150 }
  ];

  const tabData = [
    {
      label: `Links (${bulkArchiveResult.activeLinks.length})`,
      data: bulkArchiveResult.activeLinks,
      columns: linkColumns
    },
    {
      label: `Archived (${bulkArchiveResult.archivedLinks.length})`,
      data: bulkArchiveResult.archivedLinks,
      columns: linkColumns
    },
    {
      label: `No Link (${bulkArchiveResult.unlinkedRpmAccounts.length})`,
      data: bulkArchiveResult.unlinkedRpmAccounts,
      columns: rpmAccountColumns
    },
    {
      label: `ID Not Found (${bulkArchiveResult.missingRpmAccounts.length})`,
      data: bulkArchiveResult.missingRpmAccounts.map((id) => ({ rpmAccountId: id })),
      columns: missingAccountColumns
    }
  ];

  return (
    <>
      <div className="mt-8 ml-6 -mb-4">
        <Link
          className="text-indigo inline-block relative"
          title="Back to Links"
          to="/link">
          <div className="flex items-center text-small">
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
            &nbsp; Links
          </div>
        </Link>
      </div>

      <PageLayout
        dataTestId="bulk-archive-links-page"
        pageTitle="Bulk Archive Links">
        {/* Step 1 */}
        {currentStep === 1 && (
          <DetailViewCard
            enableCollapse
            title="Step 1 of 3: Identify RPM Account IDs"
            className="mb-5"
            cardActions={
              <button
                data-testid="next-button"
                disabled={isNextDisabled}
                onClick={handleNext}
                className={isNextDisabled ? DEFAULT_CANCEL_BUTTON_STYLE : DEFAULT_ADVANCE_BUTTON_STYLE}
                type="button">
                {isSubmitting ? 'Loading...' : 'Next'}
              </button>
            }>
            {isSubmitting ? (
              <div className="flex justify-center items-center p-8">
                <ComponentSpinner label="Analyzing RPM Account IDs..." />
              </div>
            ) : (
              <div className="flex flex-col space-y-4 max-w-2xl p-4">
                <TextBlock
                  size="md"
                  color="grey9">
                  RPM Account List
                </TextBlock>

                <TextField
                  error={getNonEmptyIdsCount() > 1000}
                  label={getNonEmptyIdsCount() > 1000 ? 'Maximum 1,000 IDs allowed' : ''}
                  multiline
                  rows={8}
                  fullWidth
                  placeholder="RPM Account IDs, one per line, maximum of 1,000."
                  disabled={isSubmitting}
                  value={rpmAccountIds.join('\n')}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow numbers and newlines
                    const filteredValue = value.replace(/[^0-9\n]/g, '');

                    if (filteredValue.length === 0) {
                      setRpmAccountIds([]);
                    } else {
                      // Split by newlines but preserve empty lines for proper structure
                      const ids = filteredValue.split('\n');
                      setRpmAccountIds(ids);
                    }
                  }}
                  helperText={`${getNonEmptyIdsCount()} RPM Account IDs entered`}
                />
              </div>
            )}
          </DetailViewCard>
        )}

        {/* Steps 2 & 3 - Combined Preview/Results */}
        {(currentStep === 2 || currentStep === 3) && (
          <DetailViewCard
            enableCollapse
            title={currentStep === 2 ? 'Step 2 of 3: Preview' : 'Done'}
            className="mb-5"
            cardActions={archiveButton}>
            <div className="flex flex-col space-y-4 p-4">
              <Tabs
                value={selectedTab}
                onChange={(_e, newValue) => setSelectedTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  backgroundColor: '#f5f5f5',
                  '& .MuiTab-root': {
                    textTransform: 'none' // Prevent all caps
                  },
                  '& .MuiTab-root:not(.Mui-selected)': {
                    color: '#000000' // Unselected tab text color (black)
                  },
                  '& .MuiTab-root.Mui-selected': {
                    color: 'primary.main' // Selected tab text color (blue to match indicator)
                  },
                  '& .MuiTabs-indicator': {
                    // Keep default indicator color
                  }
                }}>
                {tabData.map((tab, index) => (
                  <Tab
                    key={index}
                    label={tab.label}
                  />
                ))}
              </Tabs>

              <div style={{ minHeight: 400, width: '100%', position: 'relative' }}>
                {isSubmitting && currentStep === 2 && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                    <ComponentSpinner
                      sx={{ mt: '0', m: '0' }}
                      label="Archiving links..."
                    />
                  </div>
                )}
                {tabData.map((tab, index) => (
                  <TabPanel
                    key={index}
                    value={selectedTab}
                    index={index}>
                    <DataGridPro
                      pagination
                      pageSizeOptions={[100, 200, 500]}
                      rows={tab.data}
                      columns={tab.columns}
                      getRowId={(row) => row.id ?? row.accountId ?? row.rpmAccountId ?? 0} // Fallbacks for different data types
                      disableRowSelectionOnClick
                      disableColumnFilter
                      disableColumnMenu
                      disableColumnPinning
                      disableColumnReorder
                      showColumnVerticalBorder
                      showCellVerticalBorder
                      sx={{
                        '& .MuiDataGrid-cell': {
                          fontSize: '0.875rem'
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#f5f5f5',
                          fontSize: '0.875rem'
                        }
                      }}
                    />
                  </TabPanel>
                ))}
              </div>
            </div>
          </DetailViewCard>
        )}
      </PageLayout>

      {/* Error Modal - keep as Dialog for error display */}
      <Dialog
        data-testid="bulk-archive-error-modal"
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>
          <TextBlock
            weight="bold"
            size="lg"
            color="grey9">
            Error Processing Links
          </TextBlock>
        </DialogTitle>
        <DialogContent>
          <TextBlock
            size="md"
            color="grey9">
            {errorMessage}
          </TextBlock>
        </DialogContent>
        <DialogActions>
          <button
            className={DEFAULT_ADVANCE_BUTTON_STYLE}
            onClick={() => setErrorMessage('')}>
            OK
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
};
